import { Icon, IconType, SmallButton, H5, H6 } from "@sellar-io/components";
import { useStorefrontPromotion } from "./StorefrontPromotionProvider.shared";
import { getPercentageSpendRequirementMet } from "./getPercentageSpendRequirementMet";
import {
  BasketContextActionEnum,
  BasketOrderItem,
  useBasketContext,
} from "../../../../../hooks";
import {
  calculateItemTotal,
  formatCurrency,
  pluralize,
} from "@sellar-io/common";
import { PromotionListResponseType } from "@sellar-io/api-v2/types";

import { twMerge } from "tailwind-merge";
import db from "../../../../../db";
import { isPromotionalOrderItem } from "./isPromotionalOrderItem";
import { useState } from "react";
import { StorefrontPromotionCollapsible } from "./StorefrontPromotionCollapsible";
import { getCanPromotionRewardItemBeClaimed } from "./getCanPromotionRewardItemBeClaimed";
import { sortPromotionRewardItemsBySpendThreshold } from "./sortPromotionRewardItemsBySpendThreshold";

type Promotion = PromotionListResponseType[number];

type PromotionRewardItem = Promotion["PromotionRewardItems"][number];

export const StorefrontPromotionBasketLayout = ({
  priceTierId,
}: {
  priceTierId: number | undefined;
}) => {
  const [{ order }, dispatchBasket] = useBasketContext();

  const {
    promotions,
    claimedPromotionRewardItemIds,
    claimPromotionRewardItem,
    unclaimPromotionRewardItem,
    applyEligiblePromotionRewards,
  } = useStorefrontPromotion();

  const nonPromotionBasketOrderItems =
    order?.OrderItems?.filter((orderItem) => {
      return !isPromotionalOrderItem(orderItem);
    }) ?? [];

  const basketSubtotal = calculateItemTotal(nonPromotionBasketOrderItems);

  const eligiblePromotions = promotions
    .map((promotion) => {
      const updatedPromotionRewardItems = promotion.PromotionRewardItems.filter(
        (promotionRewardItem: PromotionRewardItem) => {
          return (
            getPercentageSpendRequirementMet({
              spendThreshold: promotionRewardItem.spendThreshold,
              basketSubtotal,
            }) === 100
          );
        },
      );
      return {
        ...promotion,
        PromotionRewardItems: updatedPromotionRewardItems,
      };
    })
    .filter((promotion) => promotion.PromotionRewardItems.length > 0);

  const handleApplyPromotionsToBasket = (
    claimedPromotionRewardItemIds: number[],
  ) => {
    let newBasketOrder = order
      ? applyEligiblePromotionRewards({
          order,
          claimedPromotionRewardItemIds,
        })
      : order;

    const updatedOrder = {
      ...newBasketOrder,
      RetailerId: newBasketOrder?.RetailerId ?? 0,
    };

    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: updatedOrder,
    });

    // This allows us to persist changes between reloads
    db.draftOrders.put(updatedOrder);
  };

  const nbEligibleRewards = eligiblePromotions.flatMap(
    (promotion) => promotion.PromotionRewardItems,
  ).length;

  if (!priceTierId || promotions.length === 0 || nbEligibleRewards <= 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="mx-4 flex items-center gap-2 rounded-lg border border-purple-800 p-2">
        <Icon
          icon="CHECKBOX_MARKED_CIRCLE"
          className="text-purple-800"
          size={1.6}
        />
        <div className="text-sm text-zinc-800">
          Congrats! You've unlocked{" "}
          <strong>
            {nbEligibleRewards} {pluralize("reward", nbEligibleRewards)}
          </strong>
          . Select the ones you'd like to add to your basket.
        </div>
      </div>
      <StorefrontPromotionCollapsible
        defaultOpen
        trigger={({ open }) => (
          <div
            className={twMerge(
              "flex p-4",
              nbEligibleRewards > 0 && "border-b border-b-purple-800",
            )}
          >
            <PromotionCollapsibleHeader
              nbRewards={nbEligibleRewards}
              title={open ? "Hide" : "View"}
              iconType={open ? "CHEVRON_UP" : "CHEVRON_DOWN"}
            />
          </div>
        )}
      >
        <div className="flex flex-col gap-4">
          {eligiblePromotions.map((promotion) => {
            return (
              <PromotionItem
                key={promotion.id}
                promotionId={promotion.id}
                promotions={promotions}
                promotionRewardItems={sortPromotionRewardItemsBySpendThreshold({
                  promotionRewardItems: promotion.PromotionRewardItems,
                })}
                nonPromotionBasketOrderItems={nonPromotionBasketOrderItems}
                claimPromotionRewardItem={claimPromotionRewardItem}
                claimedPromotionRewardItemIds={claimedPromotionRewardItemIds}
                getCanPromotionRewardItemBeClaimed={
                  getCanPromotionRewardItemBeClaimed
                }
                unclaimPromotionRewardItem={unclaimPromotionRewardItem}
                handleApplyPromotionsToBasket={handleApplyPromotionsToBasket}
                basketSubtotal={basketSubtotal}
              />
            );
          })}
        </div>
      </StorefrontPromotionCollapsible>
    </div>
  );
};

type PromotionLineItemProps = {
  promotionId: number;
  promotionRewardItem: PromotionRewardItem;
  claimedPromotionRewardItemIds: number[];
  onClaimPromotionRewardItem: () => void;
  onUnclaimPromotionRewardItem: () => void;
};

const PromotionLineItem = ({
  promotionRewardItem,
  claimedPromotionRewardItemIds,
  onClaimPromotionRewardItem,
  onUnclaimPromotionRewardItem,
}: PromotionLineItemProps) => {
  const hasPromotionRewardItemBeenClaimed =
    claimedPromotionRewardItemIds.includes(promotionRewardItem.id);
  const [productName, productFormat] = (
    promotionRewardItem.Product.nameV2 ??
    promotionRewardItem.Product.name ??
    ""
  ).split("•");

  const formattedPrice =
    promotionRewardItem.price === 0
      ? "FREE"
      : formatCurrency(promotionRewardItem.price);

  const productAndQuantityDescription =
    promotionRewardItem.quantity > 1
      ? `${promotionRewardItem.quantity}x ${productName?.trim()}`
      : productName?.trim();

  return (
    <div key={promotionRewardItem.id} className="flex flex-col">
      <div className="flex justify-between">
        <div className="flex flex-col">
          <H5>{productAndQuantityDescription}</H5>
          <H6>{productFormat?.trim()}</H6>
        </div>
        <H5>{formattedPrice}</H5>
      </div>
      <div className="flex justify-end">
        {hasPromotionRewardItemBeenClaimed ? (
          <SmallButton
            color="slim"
            disabled={false}
            className="bg-zinc-200"
            onClick={onUnclaimPromotionRewardItem}
          >
            Remove
          </SmallButton>
        ) : (
          <SmallButton color="slim" onClick={onClaimPromotionRewardItem}>
            Add to basket
          </SmallButton>
        )}
      </div>
    </div>
  );
};

type PromotionCollapsibleHeaderProps = {
  nbRewards: number;
  iconType: IconType;
  title: string;
};
const PromotionCollapsibleHeader = ({
  nbRewards,
  iconType,
  title,
}: PromotionCollapsibleHeaderProps) => {
  return (
    <div className="flex w-full cursor-pointer justify-between">
      <strong>
        {title} {pluralize("reward", nbRewards)} ({nbRewards})
      </strong>
      <Icon icon={iconType} />
    </div>
  );
};

type PromotionItemProps = Pick<
  ReturnType<typeof useStorefrontPromotion>,
  | "unclaimPromotionRewardItem"
  | "claimPromotionRewardItem"
  | "claimedPromotionRewardItemIds"
> & {
  promotionId: number;
  promotionRewardItems: PromotionRewardItem[];
  nonPromotionBasketOrderItems: BasketOrderItem[];
  promotions: Promotion[];
  basketSubtotal: number;
  handleApplyPromotionsToBasket: (
    claimedPromotionRewardItemIds: number[],
  ) => void;
  getCanPromotionRewardItemBeClaimed: typeof getCanPromotionRewardItemBeClaimed;
};
const PromotionItem = ({
  promotionId,
  promotionRewardItems,
  claimedPromotionRewardItemIds,
  nonPromotionBasketOrderItems,
  basketSubtotal,
  promotions,
  handleApplyPromotionsToBasket,
  getCanPromotionRewardItemBeClaimed,
  claimPromotionRewardItem,
  unclaimPromotionRewardItem,
}: PromotionItemProps) => {
  const [warningMessage, setWarningMessage] = useState("");

  const promotionName =
    promotions.find((promotion) => promotion.id === promotionId)?.name ??
    "Promotion";

  const rewardWithSpendThresholdNotMet = promotions
    .filter((promotion) => promotion.id === promotionId)
    .flatMap((promotion) => promotion.PromotionRewardItems || [])
    .find(
      (rewardItem) =>
        getPercentageSpendRequirementMet({
          spendThreshold: rewardItem.spendThreshold,
          basketSubtotal,
        }) !== 100,
    );

  const leftToSpend = rewardWithSpendThresholdNotMet
    ? Math.max(
        0,
        rewardWithSpendThresholdNotMet.spendThreshold - basketSubtotal,
      )
    : 0;

  const spendMessage =
    rewardWithSpendThresholdNotMet && leftToSpend
      ? `Spend ${formatCurrency(leftToSpend)} more to unlock: ${rewardWithSpendThresholdNotMet.Product.nameV2 || rewardWithSpendThresholdNotMet.Product.name}`
      : "";

  return (
    <div key={promotionId} className="flex flex-col">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex w-fit items-center gap-2 rounded-full bg-purple-100 px-2 py-1 text-sm text-purple-800">
          <Icon size={0.7} icon="PARTY_POPPER" />
          <span>{promotionName}</span>
        </div>
        <div className="flex flex-col gap-4">
          {promotionRewardItems.map((promotionRewardItem) => {
            return (
              <PromotionLineItem
                key={promotionRewardItem.id}
                promotionRewardItem={promotionRewardItem}
                promotionId={Number(promotionId)}
                claimedPromotionRewardItemIds={claimedPromotionRewardItemIds}
                onUnclaimPromotionRewardItem={() => {
                  setWarningMessage("");
                  unclaimPromotionRewardItem({
                    promotionRewardItemIds: [promotionRewardItem.id],
                    onComplete: handleApplyPromotionsToBasket,
                  });
                }}
                onClaimPromotionRewardItem={() => {
                  const {
                    canBeClaimed: canPromotionRewardItemBeClaimed,
                    message,
                  } = getCanPromotionRewardItemBeClaimed({
                    promotionRewardItemId: promotionRewardItem.id,
                    nonPromotionBasketOrderItems,
                    claimedPromotionRewardItemIds,
                    promotions,
                  });

                  if (!canPromotionRewardItemBeClaimed) {
                    let warningMessage = "Unable to claim this reward";
                    switch (message) {
                      case "REWARD_RULE_MET": {
                        warningMessage =
                          "Choose 1 reward only from this promotion";
                        break;
                      }
                      case "SPEND_THRSHOLD_NOT_MET": {
                        warningMessage = `You need to spend up to ${formatCurrency(promotionRewardItem.spendThreshold)} to claim this reward`;
                        break;
                      }
                      case "MISSING_PROMOTION":
                      case "MISSING_PROMOTION_REWARD_ITEM":
                      case "PROMOTION_REWARD_ITEM_ALREADY_CLAIMED":
                      case "UNKNOWN_ERROR":
                    }

                    setWarningMessage(warningMessage);

                    return;
                  }
                  setWarningMessage("");

                  claimPromotionRewardItem({
                    promotionRewardItemIds: [promotionRewardItem.id],
                    onComplete: handleApplyPromotionsToBasket,
                  });
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {warningMessage && (
          <div className="flex items-center gap-1 px-2 text-sm">
            <div className="min-w-fit">
              <Icon icon="ALERT" size={0.7} className="text-amber-500" />
            </div>
            <span>{warningMessage}</span>
          </div>
        )}
        <hr className="h-0 border-b border-b-purple-800" />
        {spendMessage && (
          <div className="flex items-center gap-1 px-4 text-sm">
            <div className="min-w-fit">
              <Icon icon="STAR" size={0.7} className="text-purple-800" />
            </div>
            <span>{spendMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};
